import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { COLORS } from './constants';
import { TeamRow, DashboardNavState, CustomDateRange, WeeklyDataPoint, FeatureUsagePoint, TagOption } from './types';
import { DashboardApiService } from '../../../../services/dashboard.service';
import { StatCard } from '../../../../components/groups-teams-dashboard/stat-card';
import { BreadcrumbNav } from '../../../../components/groups-teams-dashboard/breadcrumb-nav';
import { FilterBar } from '../../../../components/groups-teams-dashboard/filter-bar';
import { WeeklyBarChart } from '../../../../components/groups-teams-dashboard/weekly-bar-chart';
import { FeatureBarChart } from '../../../../components/groups-teams-dashboard/feature-bar-chart';
import { FullWidthProgressBar } from '../../../../components/groups-teams-dashboard/progress-bar';
import { TeamsTable } from '../../../../components/groups-teams-dashboard/teams-table';
import { DashboardSkeleton } from '../../../../components/groups-teams-dashboard/dashboard-skeleton';
import { exportTeamsCsv } from './export-csv';

interface Level2TeamsProps {
    groupId: string;
    groupName: string;
    onNavigate: (nav: Partial<DashboardNavState>) => void;
}

export const Level2Teams: React.FC<Level2TeamsProps> = ({ groupId, groupName, onNavigate }) => {
    const [timePeriod, setTimePeriod] = useState('7d');
    const [customDateRange, setCustomDateRange] = useState<CustomDateRange>({ startDate: null, endDate: null });
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [appliedTags, setAppliedTags] = useState<string[]>([]);
    const [selectedUserTags, setSelectedUserTags] = useState<string[]>([]);
    const [appliedUserTags, setAppliedUserTags] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterLoading, setFilterLoading] = useState(false);

    // Data state
    const [teams, setTeams] = useState<TeamRow[]>([]);
    const [summary, setSummary] = useState({ teams: 0, users: 0, active: 0, inactive: 0, activePercent: 0 });
    const [weeklyActive, setWeeklyActive] = useState<WeeklyDataPoint[]>([]);
    const [featureUsage, setFeatureUsage] = useState<FeatureUsagePoint[]>([]);
    const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
    const [userTagOptions, setUserTagOptions] = useState<TagOption[]>([]);

    // Initial load: fetch everything
    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const startDate = timePeriod === 'custom' && customDateRange.startDate
                ? customDateRange.startDate.toISOString() : undefined;
            const endDate = timePeriod === 'custom' && customDateRange.endDate
                ? customDateRange.endDate.toISOString() : undefined;

            const [teamsData, weekly, features, tags, uTags] = await Promise.all([
                DashboardApiService.fetchTeams(groupId, timePeriod, startDate, endDate, selectedTags),
                DashboardApiService.fetchWeeklyActive('group', groupId),
                DashboardApiService.fetchFeatureUsage(timePeriod, 'group', groupId, startDate, endDate),
                DashboardApiService.fetchTagOptions('team'),
                DashboardApiService.fetchTagOptions('user'),
            ]);

            setTeams(teamsData.teams);
            setSummary(teamsData.summary);
            setWeeklyActive(weekly);
            setFeatureUsage(features);
            setTagOptions(tags);
            setUserTagOptions(uTags);
        } catch (err) {
            console.error('Failed to fetch teams data:', err);
        } finally {
            setLoading(false);
        }
    }, [groupId]);

    // Filter change: only re-fetch date/tag-dependent data (skip weeklyActive & tagOptions)
    const fetchFiltered = useCallback(async (
        period: string,
        dateRange: CustomDateRange,
        tags: string[],
        userTags: string[]
    ) => {
        setFilterLoading(true);
        try {
            const startDate = period === 'custom' && dateRange.startDate
                ? dateRange.startDate.toISOString() : undefined;
            const endDate = period === 'custom' && dateRange.endDate
                ? dateRange.endDate.toISOString() : undefined;

            const [teamsData, features] = await Promise.all([
                DashboardApiService.fetchTeams(groupId, period, startDate, endDate, tags, userTags),
                DashboardApiService.fetchFeatureUsage(period, 'group', groupId, startDate, endDate),
            ]);

            setTeams(teamsData.teams);
            setSummary(teamsData.summary);
            setFeatureUsage(features);
        } catch (err) {
            console.error('Failed to fetch filtered data:', err);
        } finally {
            setFilterLoading(false);
        }
    }, [groupId]);

    useEffect(() => {
        fetchAll();
    }, [groupId]); // Re-fetch when group changes

    const handleApply = () => {
        setAppliedTags([...selectedTags]);
        setAppliedUserTags([...selectedUserTags]);
        fetchFiltered(timePeriod, customDateRange, selectedTags, selectedUserTags);
    };

    const handleClear = () => {
        setTimePeriod('7d');
        setCustomDateRange({ startDate: null, endDate: null });
        setSelectedTags([]);
        setAppliedTags([]);
        setSelectedUserTags([]);
        setAppliedUserTags([]);
        fetchFiltered('7d', { startDate: null, endDate: null }, [], []);
    };

    const handleRemoveAppliedTag = (tag: string) => {
        const updated = selectedTags.filter((t) => t !== tag);
        setSelectedTags(updated);
        setAppliedTags(updated);
        fetchFiltered(timePeriod, customDateRange, updated, appliedUserTags);
    };

    const handleRemoveAppliedUserTag = (tag: string) => {
        const updated = selectedUserTags.filter((t) => t !== tag);
        setSelectedUserTags(updated);
        setAppliedUserTags(updated);
        fetchFiltered(timePeriod, customDateRange, appliedTags, updated);
    };

    const handleTeamClick = (team: TeamRow) => {
        onNavigate({
            level: 3,
            teamId: team.id,
            teamName: team.name,
        });
    };

    const goBackToGroups = () => {
        onNavigate({
            level: 1,
            groupId: null,
            groupName: null,
            teamId: null,
            teamName: null,
        });
    };

    if (loading) {
        return <DashboardSkeleton statCards={4} tableColumns={6} tableRows={5} showProgressBar />;
    }

    return (
        <Box sx={{ opacity: filterLoading ? 0.5 : 1, transition: 'opacity 0.2s', pointerEvents: filterLoading ? 'none' : 'auto' }}>
            {/* Breadcrumb */}
            <BreadcrumbNav
                segments={[
                    { label: 'Groups & Teams', onClick: goBackToGroups },
                    { label: groupName },
                ]}
            />

            {/* Page Header */}
            <Box sx={{ marginBottom: '16px' }}>
                <Box component="h1" sx={{ fontSize: '20px', fontWeight: 600, margin: 0, fontFamily: 'Poppins, sans-serif' }}>
                    {groupName}
                </Box>
                <Box sx={{ fontSize: '12px', color: COLORS.grayDark, marginTop: '2px' }}>
                    {summary.teams} teams · {summary.users.toLocaleString()} users · Click any team to see user details.
                </Box>
            </Box>

            {/* Filters */}
            <FilterBar
                timePeriod={timePeriod}
                onTimePeriodChange={setTimePeriod}
                customDateRange={customDateRange}
                onCustomDateRangeChange={setCustomDateRange}
                tagOptions={tagOptions}
                selectedTags={selectedTags}
                onTagsChange={setSelectedTags}
                userTagOptions={userTagOptions}
                selectedUserTags={selectedUserTags}
                onUserTagsChange={setSelectedUserTags}
                onApply={handleApply}
                onClear={handleClear}
                onExport={() => exportTeamsCsv(teams, groupName)}
                appliedTags={appliedTags}
                onRemoveAppliedTag={handleRemoveAppliedTag}
                appliedUserTags={appliedUserTags}
                onRemoveAppliedUserTag={handleRemoveAppliedUserTag}
            />

            {/* Summary Stats */}
            <Box sx={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <StatCard value={summary.teams} label="Teams" />
                <StatCard value={summary.users} label="Users" />
                <StatCard
                    value={summary.active}
                    label="Active"
                    valueColor={COLORS.primary}
                />
                <StatCard
                    value={summary.inactive}
                    label="Inactive"
                    valueColor={COLORS.red}
                />
            </Box>

            {/* Progress Bar */}
            <FullWidthProgressBar percent={summary.activePercent} />

            {/* Charts */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '14px',
                    marginBottom: '16px',
                }}
            >
                <WeeklyBarChart
                    data={weeklyActive}
                    title="Weekly Active Rate"
                    subtitle={`${groupName} · Last 12 weeks`}
                />
                <FeatureBarChart
                    data={featureUsage}
                    title="Feature Usage"
                    subtitle={`${groupName} · ${timePeriod === '7d' ? 'Last 7 days' : timePeriod}`}
                />
            </Box>

            {/* Teams Table */}
            <TeamsTable
                teams={teams}
                onTeamClick={handleTeamClick}
            />
        </Box>
    );
};
